import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { track } from '@/shared/analytics';

import {
  favoriteOpportunity,
  unfavoriteOpportunity,
  opportunitiesRoomInfiniteQuery,
  opportunitiesRoomKpiQuery,
  OPPORTUNITIES_QUERY_KEYS,
  type OpportunitiesRoomQueryFilters,
} from '@/entities/opportunity';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { logger } from '@/lib/utils/logger';
import { notificationService } from '@/lib/services/notification.service';
import { useLastConnectedAt } from '../useLastConnectedAt';

import type { OpportunityCardItem } from '@/entities/opportunity';
import type { OpportunitiesRoomKpi } from '../types';

/**
 * Data hook for the Opportunities Room. Accepts already-shaped server-side
 * filters; intentionally knows nothing about which UI controls produce them.
 * Callers (e.g. OpportunitiesRoomLayout) own the mapping from UI state to
 * query params — keep that translation there, not here.
 */
export function useOpportunitiesRoom(queryFilters?: OpportunitiesRoomQueryFilters) {
  const { t } = useTranslation('analysis');
  const workspaceId = useCurrentWorkspaceId();
  const queryClient = useQueryClient();
  const lastVisitedAt = useLastConnectedAt();

  // Filtered query for the list view
  const query = opportunitiesRoomInfiniteQuery(workspaceId, queryFilters);
  const queryKey = query.queryKey;

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteQuery(query);

  const { data: kpiData, isLoading: isKpiLoading } = useQuery(
    opportunitiesRoomKpiQuery(workspaceId, lastVisitedAt?.toISOString()),
  );

  const opportunities = useMemo(
    (): OpportunityCardItem[] => data?.pages.flatMap((p) => p.items) ?? [],
    [data],
  );

  // KPI reflects UNFILTERED totals from the server.
  const kpi = useMemo((): OpportunitiesRoomKpi => {
    const total = kpiData?.total ?? 0;
    // A null baseline means the first-ever login: the stats endpoint returns
    // newCount = 0 when `since` is absent, but everything is genuinely new, so
    // surface the full total.
    const newCount = lastVisitedAt == null ? total : (kpiData?.newCount ?? 0);
    return {
      total,
      newCount,
      lastVisitedAt,
      countByType: kpiData?.countByType ?? {},
    };
  }, [kpiData, lastVisitedAt]);

  const { mutateAsync: toggleFavoriteAsync } = useMutation({
    mutationFn: async ({
      opportunityId,
      nextFavorite,
    }: {
      opportunityId: string;
      nextFavorite: boolean;
    }) => {
      if (!workspaceId) throw new Error('Workspace ID required');
      if (nextFavorite) {
        await favoriteOpportunity(workspaceId, opportunityId);
      } else {
        await unfavoriteOpportunity(workspaceId, opportunityId);
      }
    },
    onMutate: async ({ opportunityId, nextFavorite }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: typeof data) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => {
            const next = page.items
              .map((opp) =>
                opp.opportunityId === opportunityId ? { ...opp, isFavorite: nextFavorite } : opp,
              )
              .filter((opp) =>
                queryFilters?.favoritesOnly && opp.opportunityId === opportunityId
                  ? nextFavorite
                  : true,
              );
            return { ...page, items: next };
          }),
        };
      });
      return { previous };
    },
    onSuccess: (_data, { opportunityId, nextFavorite }) => {
      if (nextFavorite) {
        track('opportunity_favorited', { opportunity_id: opportunityId });
      }
      const message = nextFavorite
        ? t('opportunitiesRoom.useOpportunitiesRoom.addedToRadar')
        : t('opportunitiesRoom.useOpportunitiesRoom.removedFromRadar');
      notificationService.showSuccess(message);
    },
    onError: (error, _vars, context) => {
      logger.error('Failed to toggle favorite:', error);
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: OPPORTUNITIES_QUERY_KEYS.byWorkspace(workspaceId),
      });
    },
  });

  const toggleFavorite = useCallback(
    (opportunityId: string, nextFavorite: boolean) =>
      toggleFavoriteAsync({ opportunityId, nextFavorite }),
    [toggleFavoriteAsync],
  );

  return {
    opportunities,
    isLoading,
    isKpiLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    kpi,
    toggleFavorite,
  };
}
